import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { loadChallengeList, loadChallengeQuizList, loadChallengeQuizParticipation, setQuizCorrectResult, setQuizCorrectResultError, clearQuizCorrectResults } from '../redux/actions/challengeActions';
import { saveNewParticipation, updateParticipation } from '../services/challengeService';
import { getCurrentUserId } from '../utils/userStorage';
import { getChallengeImagePath, preloadChallengeImages } from '../utils/challengeImageUtils';
import ParticipationTab from './ParticipationTab';
import StatusTab from './StatusTab';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getSource, qbaseAvailable, LOCAL } from '../services/challengeSource';
import { buildExport, downloadExport, importDoc } from '../services/challengeFile';
import { importDocToCloud } from '../services/challengeCloudImport';
import { loadChallengePick, saveChallengePick, loadLayout, saveLayout } from '../utils/workspaceLayout';
import { fetchChallengeDbStatus } from '../services/challengeDb';
import { kindOf, dbmsOf, isDraftChallenge } from '../utils/challengeAttr';
import { IS_STANDALONE } from '../config/features';
import { formatApiError } from '../utils/formatters';
import { hasLsAdminRole, draftToExchange } from '../utils/challengeEdit';
import ChallengeEditor from './quiz/ChallengeEditor';
import ChallengePickerModal from './quiz/ChallengePickerModal';
import '../App.css';

// axios 인스턴스 생성 (App.js와 동일한 방식)
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '/sql-developer',
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    },
    timeout: 15000,
    validateStatus: function (status) {
        return status < 500;
    }
});

/**
 * 개인 사용(로컬 챌린지)에서는 **집계 화면을 감춘다** (사용자 결정 2026-09-19).
 *
 * 참여 현황 막대그래프와 제출 현황 표는 "여럿이 같은 문제를 푼다" 는 전제 위에 있다.
 * 내부망에서 혼자 쓰는 사람에게는 자기 한 줄짜리 그래프가 화면만 차지한다.
 * 숨기는 것은 이 둘뿐이고 문제 풀이·채점은 그대로다.
 */
/** 고른 난이도를 기억하는 자리. 못 읽거나 못 쓰면 조용히 기본값으로 간다. */
const LEVELS_KEY = 'livesql_challenge_levels';
/** 튜닝형 문항 — 003 SQL 개선 · 004 인덱스 설계. 「튜닝챌린지」 메뉴는 이것이 든 챌린지만 보인다 */
// 종류·DBMS 판정은 utils/challengeAttr — 챌린지에 적어 둔 속성(CHALLENGE_ATTR_CN)이 먼저, 없을 때만 문항으로 추론한다

// mode — 'query'(쿼리챌린지, 전부) | 'tuning'(튜닝챌린지, 튜닝 문항이 든 챌린지만). 데이터는 같고 거르는 것만 다르다(2026-10-04)
const QueryChallenge = ({ selectedChallengeId: externalSelectedChallengeId, onChallengeSelect, mode = 'query', onShowPlans, draft = null, onDraftConsumed }) => {
    const isTuning = mode === 'tuning';
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const challengeList = useSelector(state => state.challenge.challengeList);
    const challengeQuizMap = useSelector(state => state.challenge.challengeQuizMap);
    const challengeParticipationMap = useSelector(state => state.challenge.challengeParticipationMap);
    const quizCorrectResultMap = useSelector(state => state.challenge.quizCorrectResultMap || {});
    const authUser = useSelector(state => state.auth?.user);

    // 선택된 참여그룹 — 목록 거르기에는 더 쓰지 않는다(보일 챌린지는 서버가 정한다, 2026-10-07). 바뀜 신호만 받아 둔다
    const [, setSelectedGroup] = useState(null);

    // 챌린지 유형 상수 정의
    const CHALLENGE_TYPES = {
        BEGINNER: { code: '01', label: 'Beginner' },
        INTERMEDIATE: { code: '02', label: 'Intermediate' },
        EXPERT: { code: '03', label: 'Expert' }
    };
    const categories = Object.values(CHALLENGE_TYPES);
    // **난이도는 여러 개를 함께 볼 수 있다** (사용자 요청 2026-09-19).
    // 하나만 고르게 하면 세 등급에 흩어진 문제를 보려고 목록을 세 번 갈아 끼워야 했다.
    // 비우는 것은 막는다 — 다 끄면 목록이 빈 채로 이유가 화면에 없다.
    //
    // **고른 값을 기억한다.** 새로고침하면 난이도가 초기화되는 바람에, 되살아난 챌린지가
    // 필터에 걸려 사라졌다 — 선택은 남아 있는데 목록에는 「챌린지를 선택하세요」가 뜨고
    // 정보 카드에는 「Level: N/A」 만 보였다(지적 2026-09-19).
    // 튜닝챌린지는 **난이도를 전부 켠 채** 시작하고 저장하지 않는다 — 튜닝 문항은 대개 상급(03)이라
    // 쿼리챌린지에서 고른 난이도(기본 Beginner)를 그대로 쓰면 목록이 비어 「없다」 로 읽힌다
    const [levels, setLevels] = useState(() => {
        if (isTuning) return categories.map(cat => cat.code);
        try {
            const saved = JSON.parse(localStorage.getItem(LEVELS_KEY) || 'null');
            const valid = Array.isArray(saved) ? saved.filter(c => categories.some(cat => cat.code === c)) : [];
            if (valid.length) return valid;
        } catch (_e) { /* 저장소를 못 읽어도 화면은 떠야 한다 */ }
        return [categories[0].code];
    });

    // 공개/비공개 필터링 상태 — **누구에게 보일지는 서버가 정한다**(QBase 공개 범위, 2026-10-07: 작성자·관리자·전체·그룹 구성원·지정 사용자).
    // 예전에는 비공개 챌린지를 「참여그룹을 골랐을 때만」 보이게 화면이 걸렀다 — 이제 서버가 준 것은 다 볼 수 있는 것이라 둘 다 켠 채 시작한다
    const [showPublic, setShowPublic] = useState(true);
    const [showPrivate, setShowPrivate] = useState(true);
    // 챌린지 선택 팝업(검색·필터·등록일·참여자·내 기록·좋아요)
    const [pickerOpen, setPickerOpen] = useState(false);

    // 튜닝챌린지 DBMS 필터 — '' 는 전체. 새로고침해도 이어진다(utils/workspaceLayout)
    const [dbmsFilter, setDbmsFilterState] = useState(() => loadLayout().tuningDbms || '');
    const setDbmsFilter = useCallback((v) => { setDbmsFilterState(v); saveLayout({ tuningDbms: v }); }, []);

    // 선택된 카테고리의 챌린지 목록 (공개여부 필터링 포함) - 함수로 계산
    // 튜닝챌린지면 그 위에 「튜닝 문항이 들었나」 를 건다 — 문항 목록을 아직 안 읽은 챌린지는 확인될 때까지 뺀다
    const getBaseChallenges = () => {
        return challengeList.filter(ch => {
            // 튜닝챌린지는 DBMS 로도 나눈다(2026-10-05) — 문항이 그 DBMS 의 교육DB 에서 채점된다. DBMS 가 없는 예전 챌린지는 Oracle
            if (isTuning && dbmsFilter && dbmsOf(ch) !== dbmsFilter) {
                return false;
            }
            // 난이도 필터링 — 고른 등급 중 하나에 들면 보인다
            if (!levels.includes(ch.CHALLENGE_LEVEL_DCD)) {
                return false;
            }

            // 작성중(2026-10-06) — 작성자에게만, 공개/비공개 필터와 무관하게 보인다(만든 사람이 자기 챌린지를 못 찾으면 안 된다).
            // 「가져온 것」은 백엔드가 이미 숨겨서 오고(challengeVisibility.js), 이 판정은 QBase 쪽에 섞여 온 것을 위한 표시 필터다
            if (isDraftChallenge(ch)) {
                return IS_STANDALONE || (!!authUser?.usrid && String(ch.MGR_USRID || '') === String(authUser.usrid));
            }

            // 공개/비공개 필터링
            const isPublic = ch.PUBLIC_YN === 'Y';
            const isPrivate = ch.PUBLIC_YN === 'N';

            if (isPublic && !showPublic) {
                return false;
            }
            if (isPrivate && !showPrivate) {
                return false;
            }

            return true;
        });
    };
    // 보이는 순서는 **이름(번호) 순**(2026-10-05) — 샘플 이름의 번호(①②…)가 의도한 순서다. 수준으로 먼저 나누면 초급인 ⑤ 가 ③ 앞에 섰다. 서버는 아이디 순으로 주는데, 전문가 챌린지가 옛 아이디(EDU_TUNING)를
    // 그대로 써서(제출 이력을 살리려고) EDU_TUNING_01 보다 앞에 섰다 — 초급·중급·전문가 순이 아니었다
    // 튜닝챌린지는 DBMS 로 먼저 묶는다(「전체」 일 때 Oracle·PostgreSQL 이 섞이지 않게). 이름은 코드 순 — 로캘 비교는
    // 원 문자 번호(⑥⑦⑧⑨)를 순서대로 놓지 않는다
    const dbmsRank = (ch) => (isTuning ? (dbmsOf(ch) === 'oracle' ? 0 : 1) : 0);
    const byCode = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
    const baseChallenges = getBaseChallenges().slice().sort((a, b) =>
        (dbmsRank(a) - dbmsRank(b))
        || byCode(String(a.CHALLENGE_NM || ''), String(b.CHALLENGE_NM || '')));
    // 튜닝 문항(003·004)이 든 챌린지는 **튜닝챌린지에만** 보인다(2026-10-05) — 쿼리챌린지 목록에 튜닝 과제가 섞여 있었다.
    // 쿼리챌린지: 문항을 아직 안 읽은 챌린지는 보여 두되, DBMS 표시가 있는 것(튜닝 샘플)은 읽기 전에도 뺀다 — 잠깐 보였다 사라지지 않게
    const challenges = isTuning
        ? baseChallenges.filter(ch => kindOf(ch, challengeQuizMap[ch.QRY_CHALLENGE_ID]) === 'tuning')
        : baseChallenges.filter((ch) => {
            const quizzes = challengeQuizMap[ch.QRY_CHALLENGE_ID];
            // 아직 모르는 것(속성 없음·문항 안 읽음)은 보여 두고, 읽어서 튜닝이면 뺀다
            const kind = kindOf(ch, quizzes);
            return kind === null || kind === 'query';
        });

    // 목록에는 문항 유형이 없어(QBase 목록 API) **후보의 문항 목록을 읽어 본다** — 튜닝챌린지는 튜닝 문항이 든 것을 찾으려고,
    // 쿼리챌린지는 그것을 빼려고(두 모드 모두). 고를 때 읽는 것과 같은 함수·같은 캐시(challengeQuizMap)라 나중에 고르면
    // 다시 부르지 않는다. 한 번에 몇 개씩만.
    // 속성(CHALLENGE_ATTR_CN)으로 종류를 이미 아는 것은 읽지 않는다 — 읽는 것은 속성이 없는 챌린지뿐
    const pendingIds = baseChallenges
        .filter(ch => challengeQuizMap[ch.QRY_CHALLENGE_ID] === undefined && kindOf(ch, undefined) === null)
        .map(ch => ch.QRY_CHALLENGE_ID);
    const pendingKey = pendingIds.join('|');
    const scanningRef = useRef(false);
    // 찾는 도중 필터가 바뀌어 새 후보가 생기면 그것은 건너뛰어진다 — 한 바퀴가 끝나면 다시 본다
    const [scanTick, setScanTick] = useState(0);
    useEffect(() => {
        if (!pendingKey || scanningRef.current) return;
        scanningRef.current = true;
        const ids = pendingKey.split('|');
        (async () => {
            for (let i = 0; i < ids.length; i += 4) {
                await Promise.all(ids.slice(i, i + 4).map(id => dispatch(loadChallengeQuizList(id))));
            }
            scanningRef.current = false;
            setScanTick(n => n + 1);
        })();
    }, [pendingKey, dispatch, scanTick]);
    const tuningScanTotal = baseChallenges.length;
    const tuningScanDone = tuningScanTotal - pendingIds.length;
    // 처음 값은 **새로고침 전에 고른 것**(모드별, utils/workspaceLayout, 2026-10-05). externalSelectedChallengeId 는 여전히 무시
    const [initialPick] = useState(() => loadChallengePick(mode));
    const [selectedChallengeId, setSelectedChallengeId] = useState(initialPick.challengeId);
    const [activeSubTab, setActiveSubTab] = useState('participate'); // 참여/현황 탭 상태
    // 지금 보는 공급원 — **환경이 정한다**(웹 = 클라우드, 스탠드얼론 = 가져온 것, 사용자 결정 2026-10-06). 고르는 선택 상자는 없앴다
    const localMode = getSource() === LOCAL;

    // ── 챌린지 편집기 — 등록·수정 + 문항 (2026-10-05) ─────────────────────────
    // **누구나** 연다(사용자 결정). 막는 것은 QBase 저장 하나뿐 — 역할 LSADMIN 만, 머리 정보만(문항 저장 API 가 없다).
    // 그 밖의 저장은 파일·가져온 것으로 간다(내보내기·가져오기와 같은 형식). 표시는 편의일 뿐, 권위는 QBase 의 403 이다.
    const [editorMode, setEditorMode] = useState(null);   // null | 'new' | 'edit'
    // 에이전트 초안(App → draft prop, 2026-10-06) — 받으면 「새 챌린지」 편집기를 그 내용으로 연다. 받은 즉시 App 쪽을 비운다
    const [editorSeed, setEditorSeed] = useState(null);
    useEffect(() => {
        if (!draft) return;
        setEditorSeed({ ...draftToExchange(draft), nonce: draft.nonce });
        setEditorMode('new');
        if (onDraftConsumed) onDraftConsumed();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft?.nonce]);
    const canSaveToQbase = hasLsAdminRole(authUser?.roleCd);   // 관리자 — 남의 챌린지도 고친다(일반 사용자는 자기 것만, 편집기가 가른다)
    // 가져오기가 가는 곳 — 웹은 클라우드(사용자 결정 2026-10-06), 스탠드얼론은 로컬 저장소
    const importTo = useCallback((doc) => (qbaseAvailable()
        ? importDocToCloud(doc, { me: authUser?.usrid, isAdmin: canSaveToQbase })
        : importDoc(doc)), [authUser?.usrid, canSaveToQbase]);
    const [selectedCell, setSelectedCell] = useState(null); // 현황 탭 셀 선택 상태
    const [selectedQuizNo, setSelectedQuizNo] = useState(initialPick.quizNo); // 선택된 퀴즈 번호(새로고침 전의 것)

    // 채점 DB 사전 확인 — 웹은 학생에게 연결을 요구하지 않고 서버의 교육DB 로 채점한다(services/challengeDb).
    // 대신 그 DB 에 붙는지를 **풀기 전에** 본다: 쿼리챌린지는 시스템 풀, 튜닝은 서버의 채점 연결
    const [dbStatus, setDbStatus] = useState(null);
    const [dbChecking, setDbChecking] = useState(false);
    const checkDb = useCallback(async () => {
        setDbChecking(true);
        try { setDbStatus(await fetchChallengeDbStatus()); } catch (_e) { setDbStatus(null); } finally { setDbChecking(false); }
    }, []);
    useEffect(() => { checkDb(); }, [checkDb, mode]);
    // 튜닝은 고른 챌린지의 DBMS 교육DB 를 본다(없으면 필터의 DBMS, 그것도 없으면 Oracle). 예전 서버는 byDbms 가 없다 — Oracle 값으로
    const tuningDbms = (() => {
        const picked = (challengeList || []).find(ch => String(ch.QRY_CHALLENGE_ID) === String(selectedChallengeId));
        return picked ? dbmsOf(picked) : (dbmsFilter || 'oracle');
    })();
    const tuningReady = dbStatus && dbStatus.byDbms && dbStatus.byDbms[tuningDbms]
        ? dbStatus.byDbms[tuningDbms].ready : (dbStatus ? dbStatus.ready : undefined);
    const dbDown = !!(dbStatus && dbStatus.serverManaged && (isTuning ? tuningReady === false : dbStatus.systemPool === false));

    // 고른 챌린지·문제를 기억한다 — 쿼리·튜닝 모드마다 따로
    useEffect(() => {
        saveChallengePick(mode, { challengeId: selectedChallengeId, quizNo: selectedQuizNo });
    }, [mode, selectedChallengeId, selectedQuizNo]);
    // 기억한 챌린지가 지금 목록에 없으면(지워졌거나 출처가 바뀜) 선택을 비운다 — 목록에 없는 챌린지의 문제가 열려 있으면 모순이다.
    // 목록이 다 들어온 뒤에 본다(초대받은 비공개 챌린지는 참여그룹을 고른 뒤 늦게 들어온다 — 바로 보면 멀쩡한 선택을 지운다)
    useEffect(() => {
        if (!selectedChallengeId || !Array.isArray(challengeList) || challengeList.length === 0) return undefined;
        const timer = setTimeout(() => {
            if (!challengeList.some(ch => String(ch.QRY_CHALLENGE_ID) === String(selectedChallengeId))) {
                setSelectedChallengeId('');
                setSelectedQuizNo(null);
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, [challengeList, selectedChallengeId]);
    // 고른 것이 **이 모드의 종류가 아니면** 비운다 — 쿼리챌린지에 튜닝 챌린지가, 튜닝챌린지에 쿼리 챌린지가 골라져 있으면(예전에
    // 골라 둔 것·새로고침 복원·모드 전환 때 잘못 기억된 것) 목록에는 없는데 그 문항이 열려 있는 모순이 된다(2026-10-06: 튜닝 쪽도)
    const pickedChallenge = (challengeList || []).find(ch => String(ch.QRY_CHALLENGE_ID) === String(selectedChallengeId));
    const pickedKind = pickedChallenge ? kindOf(pickedChallenge, challengeQuizMap[selectedChallengeId]) : null;
    const pickedWrongKind = pickedKind !== null && pickedKind !== (isTuning ? 'tuning' : 'query');
    useEffect(() => {
        if (pickedWrongKind) { setSelectedChallengeId(''); setSelectedQuizNo(null); }
    }, [pickedWrongKind]);

    // **고른 챌린지가 필터에 가려지지 않게 한다.**
    //
    // 난이도 필터와 선택은 서로 모르는 상태다. 그래서 선택은 살아 있는데 그 난이도가 꺼져
    // 있으면, 목록에는 「챌린지를 선택하세요」가 뜨고 정보 카드에는 「Level: N/A」 만 보인다 —
    // 고장처럼 보이지만 사실은 필터 탓이다(지적 2026-09-19, 새로고침 뒤). 선택한 것의
    // 난이도는 **자동으로 켠다**: 사용자가 방금 고른 것을 화면이 숨기면 안 된다.
    useEffect(() => {
        if (!selectedChallengeId || !challengeList?.length) return;
        const picked = challengeList.find(ch =>
            String(ch.QRY_CHALLENGE_ID) === String(selectedChallengeId));
        const code = picked?.CHALLENGE_LEVEL_DCD;
        if (!code || levels.includes(code)) return;
        setLevels(prev => {
            if (prev.includes(code)) return prev;
            const next = [...prev, code];
            if (!isTuning) { try { localStorage.setItem(LEVELS_KEY, JSON.stringify(next)); } catch (_e) { /* 이번 세션만 */ } }
            return next;
        });
    }, [selectedChallengeId, challengeList, levels, isTuning]);

    // 외부에서 전달받은 selectedChallengeId와 동기화 (초기 마운트 제외)
    const isInitialMountForExternal = React.useRef(true);
    useEffect(() => {
        // 초기 마운트는 건너뛰기
        if (isInitialMountForExternal.current) {
            isInitialMountForExternal.current = false;
            return;
        }

        if (externalSelectedChallengeId && externalSelectedChallengeId !== selectedChallengeId) {
            setSelectedChallengeId(externalSelectedChallengeId);
        }
    }, [externalSelectedChallengeId]);

    useEffect(() => {
        // 로컬스토리지에서 선택된 참여그룹 정보 가져오기
        const updateSelectedGroup = () => {
            const storedGroup = localStorage.getItem('selectedParticipationGroup');
            if (storedGroup) {
                try {
                    setSelectedGroup(JSON.parse(storedGroup));
                } catch (error) {
                    console.error('참여그룹 정보 파싱 오류:', error);
                    setSelectedGroup(null);
                }
            } else {
                setSelectedGroup(null);
            }
        };

        // 초기 로드
        updateSelectedGroup();

        // localStorage 변경 감지 (다른 탭에서 변경된 경우)
        window.addEventListener('storage', updateSelectedGroup);

        // 같은 페이지에서 localStorage 변경 감지를 위한 커스텀 이벤트
        window.addEventListener('participationGroupChanged', updateSelectedGroup);

        // ChallengeStatus.js에서 현재 상태 요청에 응답
        const handleCurrentStateRequest = () => {
            console.log('QueryChallenge - 현재 상태 응답:', { levels, selectedChallengeId, challenges });
            if (selectedChallengeId && challenges.length > 0) {
                const selectedChallenge = challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId);
                if (selectedChallenge) {
                    // 난이도 정보 전송 — 받는 쪽 호환을 위해 단수 필드도 같이 싣는다
                    const picked = categories.filter(cat => levels.includes(cat.code));
                    window.dispatchEvent(new CustomEvent('challengeCategoryChanged', {
                        detail: { categories: picked, category: picked[0] }
                    }));

                    // 선택된 챌린지 정보 전송
                    window.dispatchEvent(new CustomEvent('challengeSelectionChanged', {
                        detail: { challenge: selectedChallenge }
                    }));
                }
            }
        };

        window.addEventListener('requestCurrentChallengeState', handleCurrentStateRequest);

        return () => {
            window.removeEventListener('storage', updateSelectedGroup);
            window.removeEventListener('participationGroupChanged', updateSelectedGroup);
            window.removeEventListener('requestCurrentChallengeState', handleCurrentStateRequest);
        };
    }, [levels, selectedChallengeId]);

    // 컴포넌트 마운트 시 챌린지 목록이 없으면 로드
    useEffect(() => {
        if (!challengeList || challengeList.length === 0) {
            console.log('QueryChallenge - 챌린지 목록이 없어서 로드 시작');
            dispatch(loadChallengeList());
        } else {
            console.log('QueryChallenge - 챌린지 목록 Redux에서 사용:', challengeList.length + '개');
        }
    }, [dispatch, challengeList]);

    useEffect(() => {
        console.log('selectedChallengeId 변경됨:', selectedChallengeId);
        if (selectedChallengeId) {
            dispatch(loadChallengeQuizList(selectedChallengeId));
            // 전체 참여 기록도 함께 로드 (리덕스 액션 사용)
            console.log('loadChallengeQuizParticipation 호출 시작:', selectedChallengeId);
            dispatch(loadChallengeQuizParticipation(selectedChallengeId));
        }
    }, [dispatch, selectedChallengeId]);

    // ── 파일로 주고받기 ─────────────────────────────────────────────────
    // 내부망에는 QBase 가 없다. 문제를 파일로 옮기면 로컬 저장소에 들어가고 화면은 그대로 돈다.
    const fileInputRef = useRef(null);

    const handleExport = useCallback(async () => {
        try {
            const { doc, source } = await buildExport();
            if (!doc.challengeCount) { toast.info(t('challenge:file.nothingToExport')); return; }
            downloadExport(doc);
            toast.success(t('challenge:file.exported', { n: doc.challengeCount, q: doc.quizCount, source }));
        } catch (err) {
            toast.error(t('challenge:file.exportFailed', { message: err.message }));
        }
    }, [t]);

    // throwErrors — 편집기에서 부를 때: 실패를 여기서 삼키지 않고 돌려준다(편집기가 열린 채 고친 것을 지킨다)
    const applyImport = useCallback(async (runImport, { throwErrors = false } = {}) => {
        try {
            const res = await runImport();
            if (qbaseAvailable()) {
                // 웹 — 클라우드에 넣었다. 만든 것·갱신한 것·실패한 것을 따로 말한다(한 건이 실패해도 나머지는 들어간다)
                toast.success(t('challenge:file.cloudImported', { n: res.challengeCount, q: res.quizCount, created: res.created, updated: res.updated }));
                for (const f of (res.failed || []).slice(0, 5)) toast.error(t('challenge:file.cloudFailed', { name: f.name, message: f.message }));
                if (res.stoppedAtLimit) toast.warning(t('challenge:file.cloudStopped', { n: res.stoppedAtLimit }));
            } else {
                toast.success(t('challenge:file.imported', { n: res.challengeCount, q: res.quizCount }));
            }
            // 첨부처럼 **따라오지 않는 것**은 조용히 넘기지 않는다
            for (const w of (res.warnings || []).slice(0, 3)) toast.warning(w);
            handleRefresh();
        } catch (err) {
            if (throwErrors) throw err;
            toast.error(t('challenge:file.importFailed', { message: formatApiError(err, t) }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    const handleImportFile = useCallback((e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';      // 같은 파일을 다시 골라도 change 가 나게
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            let doc;
            try { doc = JSON.parse(reader.result); }
            catch (err) { toast.error(t('challenge:file.importFailed', { message: err.message })); return; }
            applyImport(() => importTo(doc));
        };
        reader.readAsText(file);
    }, [applyImport, t]);

    // 새로고침 핸들러
    const handleRefresh = () => {
        console.log('QueryChallenge - 수동 새로고침');
        // 1. 챌린지 목록 새로고침
        dispatch(loadChallengeList());

        // 2. 선택된 챌린지의 퀴즈 목록도 새로고침
        if (selectedChallengeId) {
            dispatch(loadChallengeQuizList(selectedChallengeId, true)); // forceRefresh = true
            // 3. 참여 기록도 새로고침 (리덕스 액션 사용)
            console.log('새로고침에서 loadChallengeQuizParticipation 호출:', selectedChallengeId);
            dispatch(loadChallengeQuizParticipation(selectedChallengeId, true)); // forceRefresh = true
        }

        // 4. 정답 쿼리 결과도 초기화 (새로고침 시 다시 실행되도록)
        if (selectedChallengeId) {
            dispatch(clearQuizCorrectResults(selectedChallengeId));
            console.log('정답 쿼리 결과 초기화됨 - 퀴즈 데이터 로드 후 자동 갱신됨');
        }
    };

    // 카테고리/그룹 변경 시 첫 번째 챌린지 자동 선택
    // 단, 초기 마운트 시에는 자동 선택하지 않음
    // 사용자가 선택을 원할 때만 선택하도록 이 기능을 비활성화
    // useEffect(() => {
    //     if (challengeList.length > 0) {
    //         const filteredChallenges = challengeList.filter(ch => {
    //             if (ch.CHALLENGE_LEVEL_DCD !== category.code) {
    //                 return false;
    //             }
    //             if (ch.PUBLIC_YN === 'N' && !selectedGroup) {
    //                 return false;
    //             }
    //             return true;
    //         });
    //         if (filteredChallenges.length > 0 && (!selectedChallengeId || !filteredChallenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId))) {
    //             const firstChallenge = filteredChallenges[0];
    //             setSelectedChallengeId(firstChallenge.QRY_CHALLENGE_ID);
    //             if (onChallengeSelect) {
    //                 onChallengeSelect(firstChallenge.QRY_CHALLENGE_ID);
    //             }
    //             window.dispatchEvent(new CustomEvent('challengeSelectionChanged', {
    //                 detail: { challenge: firstChallenge }
    //             }));
    //         }
    //     }
    // }, [category.code, selectedGroup?.GROUP_ID, showPublic, showPrivate]);

    // 챌린지 이미지 미리 로드
    useEffect(() => {
        if (challenges.length > 0) {
            const challengeIds = challenges.map(ch => ch.QRY_CHALLENGE_ID);
            preloadChallengeImages(challengeIds);
            console.log('챌린지 이미지 미리 로드:', challengeIds.length, '개');
        }
    }, [challenges]);

    // 퀴즈 목록과 참여 기록 - 리덕스에서 가져오기
    const quizList = challengeQuizMap[selectedChallengeId] || [];
    const allParticipations = challengeParticipationMap[selectedChallengeId] || [];

    // 퀴즈 통계 계산
    const TOTAL_QUIZ_COUNT = 10;
    const actualQuizCount = quizList.length > 0 ? Math.max(...quizList.map(item => item.QUIZ_NO || 0)) : 0;

    const { participantsList, quizStats } = useMemo(() => {
        // 참가자 데이터 처리
        const participants = allParticipations.reduce((acc, item) => {
            const userId = item.USRID;
            const userName = item.USER_KNME || userId;
            if (!acc[userId]) {
                acc[userId] = { name: userName, usrid: userId };
            }
            acc[userId][`q${item.QUIZ_NO}`] = item.SUCCSS_YN === 'Y' ? 'success' : 'fail';   // 언어 중립 토큰 — 비교/색상 분기에만 쓴다
            return acc;
        }, {});

        const participantsListCalculated = Object.values(participants);

        // 통계 계산
        const stats = [];
        if (actualQuizCount && actualQuizCount > 0) {
            const totalTargets = participantsListCalculated.length;

            for (let i = 1; i <= actualQuizCount; i++) {
                const quizKey = `q${i}`;
                const participantsForQuiz = participantsListCalculated.filter(p => p[quizKey] !== undefined);
                const total = participantsForQuiz.length;
                const success = participantsForQuiz.filter(p => p[quizKey] === 'success').length;
                const fail = participantsForQuiz.filter(p => p[quizKey] === 'fail').length;
                const notSubmitted = totalTargets - total;

                stats.push({
                    quiz: `Q${i}`,
                    total,
                    success,
                    fail,
                    notSubmitted,
                    totalTargets,
                    successRate: totalTargets > 0 ? Math.round((success / totalTargets) * 100) : 0,
                    failRate: totalTargets > 0 ? Math.round((fail / totalTargets) * 100) : 0,
                    notSubmittedRate: totalTargets > 0 ? Math.round((notSubmitted / totalTargets) * 100) : 0
                });
            }
        }

        return {
            participantsList: participantsListCalculated,
            quizStats: stats
        };
    }, [allParticipations, actualQuizCount]);

    /**
     * 난이도 켜고 끄기. **마지막 하나는 끄지 않는다** — 다 꺼서 목록이 비면
     * 문제가 없는 것인지 필터 탓인지 화면으로 구분할 수 없다.
     *
     * 이벤트에는 `categories`(고른 전부)와 `category`(그중 첫 개)를 함께 싣는다.
     * 받는 쪽(`ChallengeStatus`)이 아직 하나만 다루므로 그쪽을 깨지 않으려는 것이다.
     */
    const toggleLevel = (code) => {
        setLevels(prev => {
            const next = prev.includes(code)
                ? (prev.length === 1 ? prev : prev.filter(c => c !== code))
                : [...prev, code];
            if (next === prev) return prev;
            if (!isTuning) { try { localStorage.setItem(LEVELS_KEY, JSON.stringify(next)); } catch (_e) { /* 못 저장해도 이번 세션은 동작한다 */ } }
            const picked = categories.filter(cat => next.includes(cat.code));
            window.dispatchEvent(new CustomEvent('challengeCategoryChanged', {
                detail: { categories: picked, category: picked[0] }
            }));
            return next;
        });
    };

    /**
     * 선택 팝업의 후보 — 이 모드(쿼리/튜닝)의 챌린지 전부. 난이도·공개 체크는 팝업이 따로 거르므로 여기서는 종류와 작성중 규칙만.
     * 팝업에서 고르면 목록 필터(난이도·공개)를 그 챌린지가 보이게 맞춘다 — 고른 것이 목록에 없으면 「선택하세요」 로 보인다.
     */
    const pickerCandidates = (challengeList || []).filter((ch) => {
        if (isDraftChallenge(ch) && !(IS_STANDALONE || (!!authUser?.usrid && String(ch.MGR_USRID || '') === String(authUser.usrid)))) return false;
        const kind = kindOf(ch, challengeQuizMap[ch.QRY_CHALLENGE_ID]);
        return isTuning ? kind === 'tuning' : (kind === null || kind === 'query');
    });
    const pickFromPicker = (challengeId) => {
        const ch = (challengeList || []).find(x => String(x.QRY_CHALLENGE_ID) === String(challengeId));
        if (ch) {
            if (!levels.includes(ch.CHALLENGE_LEVEL_DCD)) toggleLevel(ch.CHALLENGE_LEVEL_DCD);
            if (ch.PUBLIC_YN === 'Y' && !showPublic) setShowPublic(true);
            if (ch.PUBLIC_YN !== 'Y' && !showPrivate) setShowPrivate(true);
            if (isTuning && dbmsFilter && dbmsOf(ch) !== dbmsFilter) setDbmsFilter('');
        }
        setSelectedChallengeId(String(challengeId));
        setSelectedQuizNo(null);
        if (onChallengeSelect) onChallengeSelect(String(challengeId));
        window.dispatchEvent(new CustomEvent('challengeSelectionChanged', { detail: { challenge: ch } }));
    };

    // 챌린지 선택 핸들러
    const handleChallengeSelect = (e) => {
        const challengeId = e.target.value;
        setSelectedChallengeId(challengeId);
        setSelectedQuizNo(null); // 챌린지 변경 시 선택된 퀴즈 초기화

        // 부모에게 챌린지 선택 변경을 알림
        if (onChallengeSelect) {
            onChallengeSelect(challengeId);
        }

        // 다른 컴포넌트들에게 챌린지 선택 변경을 알림
        const selectedChallenge = challenges.find(ch => ch.QRY_CHALLENGE_ID === challengeId);
        window.dispatchEvent(new CustomEvent('challengeSelectionChanged', {
            detail: { challenge: selectedChallenge }
        }));
    };

    // 퀴즈 버튼 클릭 핸들러
    const handleQuizButtonClick = (quizNo) => {
        setSelectedQuizNo(quizNo);
        setActiveSubTab('participate'); // 퀴즈 선택 시 자동으로 참여 탭으로 이동
    };

    return (
        // **패널 안에 또 테두리를 두지 않는다**(2026-09-20). 패널이 이미 제목줄과 테두리로
        // 틀을 준다 — 안쪽에 둥근 상자를 하나 더 그리니 옆의 데이터 분석 패널과 모양이 달라
        // 「일관되지 않다」로 읽혔다. 여백만 남긴다.
        <div style={{
            padding: 10,
            background: 'var(--color-bg)',
            width: '100%',
            height: '100%',
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            fontSize: 12,
        }}>
            {dbDown && (
                <div role="alert" style={{ marginBottom: 8, padding: '6px 8px', borderRadius: 4, fontSize: 11, lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 6, background: 'var(--color-quiz-warn-bg)', border: '1px solid var(--color-quiz-warn-border)', color: 'var(--color-quiz-warn-text)', animation: (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'none' : 'txn-chip-fade 160ms ease-out' }}>
                    <span style={{ flex: 1 }}>{t('challenge:db.unavailable')}</span>
                    <button onClick={checkDb} disabled={dbChecking} style={{ flexShrink: 0, fontSize: 10, padding: '2px 8px', borderRadius: 3, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', cursor: dbChecking ? 'wait' : 'pointer' }}>
                        {dbChecking ? t('challenge:tuning.notReady.checking') : t('challenge:db.retry')}
                    </button>
                </div>
            )}
            {/* 튜닝챌린지 안내 — 무엇을 걸러 보여 주는지, 찾는 중인지, 없으면 왜 없는지 */}
            {isTuning && (
                <div style={{ marginBottom: 8, padding: '6px 8px', borderRadius: 4, fontSize: 11, lineHeight: 1.5, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                    {pendingIds.length > 0
                        ? t('challenge:qc.tuningScanning', { done: tuningScanDone, total: tuningScanTotal })
                        : (challenges.length > 0
                            ? t('challenge:qc.tuningFilter', { n: challenges.length })
                            : t('challenge:qc.tuningEmpty'))}
                </div>
            )}
            {/* 상단: 카테고리/챌린지 선택 + 공개/비공개 필터 + 새로고침 버튼 */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, gap: 8 }}>
                {/* DBMS — 튜닝챌린지만. 같은 데이터라도 DBMS 마다 옵티마이저가 달라 문제가 갈린다 */}
                {isTuning && (
                    <select
                        value={dbmsFilter}
                        onChange={(e) => {
                            const v = e.target.value;
                            setDbmsFilter(v);
                            // 고른 챌린지가 새 필터에 안 맞으면 선택을 비운다 — 목록에 없는 챌린지의 문제가 열려 있으면 모순이다
                            const picked = (challengeList || []).find(ch => String(ch.QRY_CHALLENGE_ID) === String(selectedChallengeId));
                            if (v && picked && dbmsOf(picked) !== v) {
                                setSelectedChallengeId('');
                                setSelectedQuizNo(null);
                            }
                        }}
                        aria-label={t('challenge:dbms.filterLabel')}
                        title={t('challenge:dbms.filterLabel')}
                        style={{ height: 32, fontSize: 12, borderRadius: 4, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}
                    >
                        <option value="">{t('challenge:dbms.all')}</option>
                        <option value="oracle">{t('challenge:dbms.oracle')}</option>
                        <option value="postgresql">{t('challenge:dbms.postgresql')}</option>
                    </select>
                )}
                {/* 난이도 — 여러 개를 함께 켤 수 있다 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 8px', borderRadius: 4, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
                    {categories.map(cat => (
                        <label key={`category-${cat.code}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input
                                type="checkbox"
                                checked={levels.includes(cat.code)}
                                onChange={() => toggleLevel(cat.code)}
                                style={{ cursor: 'pointer' }}
                            />
                            {cat.label}
                        </label>
                    ))}
                </div>
                <select value={selectedChallengeId} onChange={handleChallengeSelect} style={{ flex: '1 1 240px', minWidth: 200, height: 32, fontSize: 12, borderRadius: 4, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
                    <option value="">{t('challenge:qc.pickChallengeOption')}</option>
                    {challenges.map(ch => <option key={`challenge-${ch.QRY_CHALLENGE_ID}`} value={ch.QRY_CHALLENGE_ID}>{(isDraftChallenge(ch) ? `[${t('challenge:edit.statuses.draft')}] ` : '') + (isTuning ? `[${t(`challenge:dbms.${dbmsOf(ch)}`)}] ${ch.CHALLENGE_NM}` : ch.CHALLENGE_NM)}</option>)}
                </select>
                {/* 찾기 — 검색·필터·등록일·참여자·내 기록·좋아요로 고르는 팝업 */}
                <button type="button" className="challenge-file-btn" onClick={() => setPickerOpen(true)} title={t('challenge:picker.openHint')}>
                    {t('challenge:picker.open')}
                </button>

                {/* 공개/비공개 필터 체크박스 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, flexShrink: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <input
                            type="checkbox"
                            checked={showPublic}
                            onChange={(e) => setShowPublic(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        {t('challenge:qc.public')}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <input
                            type="checkbox"
                            checked={showPrivate}
                            onChange={(e) => setShowPrivate(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        {t('challenge:qc.private')}
                    </label>
                </div>

                <div className="challenge-toolbar-actions" style={{ marginLeft: 'auto' }}>
                    {/* 문제 출처 — **지금 어디를 보고 있는지 화면에 보인다.** 예전에는 가져오는 순간
                        조용히 바뀌어, 왜 목록이 달라졌는지 알 수 없었다(신고 2026-09-19).
                        스탠드얼론에는 QBase 가 없어 고를 것이 없으므로 표시하지 않는다. */}
                    {/* 파일로 주고받기 — QBase 가 없는 곳으로 문제를 옮기는 길 */}
                    <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImportFile} />
                    <button className="challenge-file-btn" onClick={handleExport} title={t('challenge:file.exportHint')}>
                        {t('challenge:file.export')}
                    </button>
                    <button className="challenge-file-btn" onClick={() => fileInputRef.current?.click()} title={t('challenge:file.importHint')}>
                        {t('challenge:file.import')}
                    </button>
                    <button className="challenge-file-btn" onClick={() => { setEditorSeed(null); setEditorMode('new'); }} title={t('challenge:edit.newHint')}>
                        {t('challenge:edit.newButton')}
                    </button>
                    <button
                        className="challenge-file-btn"
                        onClick={() => setEditorMode('edit')}
                        disabled={!pickedChallenge}
                        title={pickedChallenge ? t('challenge:edit.hint') : t('challenge:qc.pickChallenge')}
                    >
                        {t('challenge:edit.button')}
                    </button>
                    <button
                        onClick={handleRefresh}
                        style={{
                            height: 28,
                            minWidth: 60,
                            padding: '0 8px',
                            background: 'var(--color-bg, #fff)',
                            color: 'var(--color-text, #333)',
                            border: '1px solid var(--color-border, #ddd)',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 11,
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            transition: 'background 0.15s',
                        }}
                        title={t('challenge:qc.refresh')}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'var(--color-bg-tertiary, #f0f0f0)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'var(--color-bg, #fff)';
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                        </svg>
                        {t('challenge:qc.refresh')}
                    </button>
                </div>
            </div>
            {/* 챌린지 정보, 퀴즈 목록, 참여 통계 - 항상 고정 높이 유지 */}
            <div style={{ display: 'flex', marginBottom: 12, gap: 8, width: '100%', height: 120, flexShrink: 0 }}>
                {/* 챌린지 정보 */}
                <div style={{ flex: '0 0 calc((100% - 16px) / 3)', minWidth: 0, border: '1px solid var(--color-border)', background: selectedChallengeId ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)', borderRadius: 4, padding: 8, height: '100%', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', overflow: 'hidden', gap: 10 }}>
                {selectedChallengeId ? (
                    <>
                    {/* 이미지 부분 */}
                    <div style={{ width: 100, height: 100, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-secondary)', borderRadius: 8, border: '2px solid var(--color-border)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <img
                            src={selectedChallengeId ? getChallengeImagePath(selectedChallengeId) : getChallengeImagePath('default')}
                            alt="Query Challenge"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                borderRadius: 6
                            }}
                            onError={(e) => {
                                e.target.src = getChallengeImagePath('1'); // 기본 이미지로 폴백
                            }}
                        />
                    </div>
                    {/* 텍스트 부분 */}
                    <div style={{ flex: 1, textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingLeft: 4 }}>
                        {challenges.length > 0 ? (
                            <>
                                {(() => {
                                    const challengeName = challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.CHALLENGE_NM || '';
                                    const parenthesesMatch = challengeName.match(/^(.+?)(\(.+\))$/);

                                    if (parenthesesMatch) {
                                        const mainTitle = parenthesesMatch[1].trim();
                                        const subtitle = parenthesesMatch[2];
                                        return (
                                            <>
                                                <div style={{ fontWeight: 'bold', marginBottom: 2, fontSize: 13, lineHeight: 1.2, color: 'var(--color-text)' }}>
                                                    {mainTitle}
                                                </div>
                                                <div style={{ fontSize: 10, lineHeight: 1.1, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                                                    {subtitle}
                                                </div>
                                            </>
                                        );
                                    } else {
                                        return (
                                            <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 13, lineHeight: 1.2, color: 'var(--color-text)' }}>
                                                {challengeName}
                                            </div>
                                        );
                                    }
                                })()}
                                <div style={{ fontSize: 11, lineHeight: 1.3, color: 'var(--color-text-secondary)', marginBottom: 2 }}>
                                    📅 {challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.BGNG_DT?.substring(0, 4)}.{challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.BGNG_DT?.substring(4, 6)}.{challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.BGNG_DT?.substring(6, 8)} ~
                                    {challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.END_DT?.substring(0, 4)}.{challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.END_DT?.substring(4, 6)}.{challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.END_DT?.substring(6, 8)}
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                                    Level: {challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId)?.CHALLENGE_LEVEL_DCD || 'N/A'}
                                </div>
                            </>
                        ) : (
                            <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{t('challenge:qc.pickChallenge')}</span>
                        )}
                    </div>
                    </>
                ) : (
                    <span style={{ color: 'var(--color-border)', fontSize: 12 }}>{t('challenge:qc.pickChallenge')}</span>
                )}
                </div>
                {/* 퀴즈 목록 */}
                <div style={{ flex: '0 0 calc((100% - 16px) / 3)', minWidth: 0, border: '1px solid var(--color-border)', background: selectedChallengeId ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)', borderRadius: 4, display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', alignItems: 'center', alignContent: 'center', height: '100%', overflow: 'hidden', padding: '8px', boxSizing: 'border-box' }}>
                {selectedChallengeId ? (
                    <>
                    {Array.from({ length: TOTAL_QUIZ_COUNT }, (_, idx) => {
                        const quizPosition = idx + 1; // 1부터 시작하는 위치
                        const quiz = quizList.find(q => parseInt(q.QUIZ_NO) === quizPosition); // QUIZ_NO를 숫자로 변환해서 비교
                        const hasData = quiz && quiz.QUIZ_NO;

                        // 참여 기록 확인 (QUIZ_NO와 USRID 모두 매칭)
                        const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();
                        const participation = hasData && allParticipations && Array.isArray(allParticipations)
                            ? allParticipations.find(p =>
                                String(p.QUIZ_NO) === String(quiz.QUIZ_NO) &&
                                String(p.USRID) === String(currentUserId)
                            ) || null
                            : null;
                        const hasParticipated = participation !== null && participation !== undefined;
                        const isCorrect = hasParticipated && participation.SUCCSS_YN === 'Y';

                        // 선택 여부 확인
                        const isSelected = selectedQuizNo === quizPosition;

                        // 스타일 결정
                        let buttonStyle = {};
                        // 그라데이션·그림자를 쓰지 않는다 (사용자 지적 2026-09-19).
                        // 상태는 **채움색 하나**로 구분하고, 테두리는 회색 계열로 통일한다 —
                        // 파란 테두리가 여기저기 튀면 어디가 강조인지 알 수 없다.
                        if (isSelected) {
                            // 선택된 퀴즈 (최우선)
                            buttonStyle = {
                                background: 'var(--color-accent)',
                                border: '1px solid var(--color-accent)',
                                color: 'white',   // 파란 채움 위 글자 — 양쪽 모드에서 같다
                            };
                        } else if (hasParticipated) {
                            // 참여한 퀴즈
                            if (isCorrect) {
                                buttonStyle = {
                                    background: 'var(--color-quiz-success-bg)',
                                    border: '1px solid var(--color-quiz-success-border)',
                                    color: 'var(--color-quiz-success-text)'
                                };
                            } else {
                                buttonStyle = {
                                    background: 'var(--color-quiz-fail-bg)',
                                    border: '1px solid var(--color-quiz-fail-border)',
                                    color: 'var(--color-quiz-fail-text)'
                                };
                            }
                        } else if (hasData) {
                            // 참여하지 않은 퀴즈
                            buttonStyle = {
                                background: 'var(--color-bg)',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-text)'
                            };
                        } else {
                            // 데이터 없는 퀴즈
                            buttonStyle = {
                                background: 'var(--color-bg)',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-border)'
                            };
                        }

                        return (
                            <button
                                key={quizPosition}
                                disabled={!hasData}
                                onClick={() => hasData && handleQuizButtonClick(quizPosition)}
                                style={{
                                    width: 'calc(20% - 5px)',
                                    height: 'calc(50% - 3px)',
                                    flexShrink: 0,
                                    ...buttonStyle,
                                    borderRadius: 4,
                                    fontWeight: isSelected || hasParticipated ? 'bold' : 500,
                                    fontSize: 10,
                                    cursor: hasData ? 'pointer' : 'default',
                                    opacity: hasData ? 1 : 0.5,
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                title={isSelected ?
                                    t('challenge:qc.selectedQuiz') :
                                    (hasParticipated ?
                                        (isCorrect ? t('challenge:qc.doneCorrect') : t('challenge:qc.doneWrong')) :
                                        (hasData ? t('challenge:qc.notParticipated') : t('challenge:qc.noQuiz')))
                                }
                            >
                                {hasData ? `Q${quiz.QUIZ_NO}` : `Q${quizPosition}`}
                                {hasParticipated && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 1,
                                        right: 1,
                                        fontSize: 7,
                                        lineHeight: 1
                                    }}>
                                        {isCorrect ? '✅' : '❌'}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                    </>
                ) : (
                    <span style={{ color: 'var(--color-border)', fontSize: 12 }}>{t('challenge:qc.pickForQuiz')}</span>
                )}
                </div>
                {/* 참여 통계 막대그래프 — 여럿이 풀 때만 뜻이 있다 (로컬 개인 사용에서는 감춘다) */}
                {!localMode && <div style={{ flex: '0 0 calc((100% - 16px) / 3)', minWidth: 0, border: '1px solid var(--color-border)', background: selectedChallengeId ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)', borderRadius: 4, padding: '8px 12px', height: '100%', display: 'flex', flexDirection: 'row', justifyContent: selectedChallengeId ? 'space-between' : 'center', alignItems: selectedChallengeId ? 'flex-start' : 'center', boxSizing: 'border-box', overflow: 'hidden', gap: 4 }}>
                {selectedChallengeId ? (
                    <>
                    {Array.from({ length: TOTAL_QUIZ_COUNT }, (_, i) => {
                        const quizIndex = i + 1;
                        const stat = quizStats[i];
                        const hasData = quizIndex <= actualQuizCount;
                        let successRate = 0, failRate = 0, notSubmittedRate = 100;

                        if (hasData && stat) {
                            successRate = stat.successRate;
                            failRate = stat.failRate;
                            notSubmittedRate = stat.notSubmittedRate;
                        }

                        return (
                            <div key={`Q${quizIndex}`} style={{
                                width: 'calc(10% - 4px)',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                position: 'relative',
                                opacity: hasData ? 1 : 0.3
                            }}>
                                {/* 세로 막대 그래프 */}
                                <div style={{
                                    width: '100%',
                                    height: 'calc(100% - 12px)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden'
                                }}>
                                    {/* 미제출 영역 (상단) */}
                                    <div style={{
                                        height: `${notSubmittedRate}%`,
                                        background: 'var(--color-bg)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 7,
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        {hasData && notSubmittedRate > 15 ? `${notSubmittedRate}%` : ''}
                                    </div>
                                    {/* 실패 영역 (중간) */}
                                    <div style={{
                                        height: `${failRate}%`,
                                        background: '#dc3545',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 7,
                                        color: 'var(--color-bg)'
                                    }}>
                                        {hasData && failRate > 15 ? `${failRate}%` : ''}
                                    </div>
                                    {/* 성공 영역 (하단) */}
                                    <div style={{
                                        height: `${successRate}%`,
                                        background: 'var(--color-accent)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 7,
                                        color: 'var(--color-bg)'
                                    }}>
                                        {hasData && successRate > 15 ? `${successRate}%` : ''}
                                    </div>
                                </div>
                                {/* 퀴즈 라벨 */}
                                <div style={{
                                    fontSize: 8,
                                    fontWeight: 'bold',
                                    marginTop: 2,
                                    color: hasData ? 'var(--color-text)' : 'var(--color-border)'
                                }}>
                                    {hasData ? `Q${quizIndex}` : ''}
                                </div>
                            </div>
                        );
                    })}
                    </>
                ) : (
                    <span style={{ color: 'var(--color-border)', fontSize: 12 }}>{t('challenge:qc.pickForStats')}</span>
                )}
                </div>}
            </div>
            {/* 참여/현황 탭 영역 */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-bg)', overflow: 'hidden' }}>
                {/* 탭 헤더 — **탭이 둘일 때만** 탭처럼 그린다.
                    제출 현황을 감춘 로컬 모드에서는 남는 것이 「참여」 하나뿐인데, 거기에 파란 글자와
                    파란 밑줄을 그리면 누를 것이 없는데도 강조된 탭으로 보인다(지적 2026-09-19).
                    강조는 밑줄 하나로만 주고 글자는 본문색을 쓴다 — 파란 선이 곳곳에 튀지 않게. */}
                {localMode ? (
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', fontSize: 13, fontWeight: 'bold', color: 'var(--color-text)' }}>
                        {t('challenge:qc.participate')}
                    </div>
                ) : (
                <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)' }}>
                    {[['participate', t('challenge:qc.participate')], ['status', t('challenge:qc.status')]].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setActiveSubTab(key)}
                            style={{
                                flex: 1,
                                padding: '12px 16px',
                                border: 'none',
                                background: activeSubTab === key ? 'var(--color-bg)' : 'transparent',
                                color: activeSubTab === key ? 'var(--color-text)' : 'var(--color-text-secondary)',
                                fontWeight: activeSubTab === key ? 'bold' : 'normal',
                                cursor: 'pointer',
                                fontSize: 13,
                                transition: 'background 150ms ease, color 150ms ease',
                                borderBottom: activeSubTab === key ? '2px solid var(--color-accent)' : '2px solid transparent'
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                )}

                {/* 탭 내용 */}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    {activeSubTab === 'participate' && (
                        <ParticipationTab
                            selectedChallengeId={selectedChallengeId}
                            challenges={challenges}
                            quizList={quizList}
                            allParticipations={allParticipations}
                            selectedQuizNo={selectedQuizNo}
                            onSubmissionSuccess={handleRefresh}
                            onShowPlans={onShowPlans}
                        />
                    )}
                    {!localMode && activeSubTab === 'status' && (
                        <StatusTab
                            allParticipations={allParticipations}
                            selectedCell={selectedCell}
                            onCellSelect={setSelectedCell}
                            selectedChallengeId={selectedChallengeId}
                            challenges={challenges}
                        />
                    )}
                </div>
            </div>
            <ChallengePickerModal
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onPick={pickFromPicker}
                candidates={pickerCandidates}
                isTuning={isTuning}
                cloud={!localMode}
                currentUsrid={authUser?.usrid || ''}
                selectedId={selectedChallengeId}
            />
            <ChallengeEditor
                open={editorMode === 'new' || (editorMode === 'edit' && !!pickedChallenge)}
                mode={editorMode === 'new' ? 'new' : 'edit'}
                challengeId={pickedChallenge ? String(pickedChallenge.QRY_CHALLENGE_ID) : undefined}
                source={localMode ? 'local' : 'qbase'}
                canSaveToQbase={canSaveToQbase}
                defaults={{ kind: isTuning ? 'tuning' : 'query', dbms: (isTuning && dbmsFilter) || 'oracle', author: authUser?.usrid }}
                seed={editorMode === 'new' ? editorSeed : null}
                onClose={() => { setEditorMode(null); setEditorSeed(null); }}
                onImportDoc={(doc) => applyImport(() => importTo(doc), { throwErrors: true })}
                currentUsrid={authUser?.usrid}
                onQbaseSaved={handleRefresh}
            />
        </div>
    );
};

export default QueryChallenge;